// CartContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { trackAddToCart } from "~/utils/metaPixel";

type CartItem = {
  id: string; // Stripe Price ID
  name: string;
  price: number;
  quantity: number;
};

type CartContextType = {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
};

// ✅ Create the context
const cartContext = createContext<CartContextType | null>(null);

// ✅ Define the provider
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  console.log("CartProvider mounted");
  const [items, setItems] = useState<CartItem[]>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("cartItems");
        return saved ? (JSON.parse(saved) as CartItem[]) : [];
      } catch (e) {
        console.warn("Failed to parse saved cart:", e);
      }
    }
    return [];
  });

  // ✅ Persist cart whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log("Saving cart items:", items);
      localStorage.setItem("cartItems", JSON.stringify(items));
    }
  }, [items]);

  // ✅ Sync cart across tabs
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key === "cartItems" && e.newValue) {
        setItems(JSON.parse(e.newValue));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ✅ Add / remove / clear handlers
  const value = useMemo(
    () => ({
      items,
      addItem(item: CartItem) {
        trackAddToCart(item);
        setItems((prev: CartItem[]) => {
          const existing = prev.find((i) => i.id === item.id);
          if (existing) {
            return prev.map((i) =>
              i.id === item.id
                ? { ...i, quantity: i.quantity + item.quantity }
                : i,
            );
          }
          return [...prev, item];
        });
      },
      removeItem(id: string) {
        setItems((prev: CartItem[]) => prev.filter((i) => i.id !== id));
      },
      clearCart() {
        setItems([]);
      },
    }),
    [items],
  );

  return <cartContext.Provider value={value}>{children}</cartContext.Provider>;
};

// ✅ Hook for consuming the context
export const useCart = (): CartContextType => {
  const context = useContext(cartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
